const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}

let graziellaRevealObserver;
const graziellaRevealTargets = new WeakMap();

function initializeGraziellaReveal(rootEl = document) {
  const elements = Array.from(rootEl.querySelectorAll('[data-gz-reveal]')).filter(
    (element) => element.dataset.gzObserved !== 'true'
  );

  if (elements.length === 0) return;

  if (
    Shopify.designMode ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !('IntersectionObserver' in window)
  ) {
    elements.forEach((element) => element.classList.add('is-visible'));
    return;
  }

  try {
    if (!graziellaRevealObserver) {
      graziellaRevealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const targets = graziellaRevealTargets.get(entry.target) || [entry.target];
            targets.forEach((target) => target.classList.add('is-visible'));
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }

    document.documentElement.classList.add('graziella-motion-ready');
    elements.forEach((element) => {
      element.dataset.gzObserved = 'true';
      const observerTarget =
        element.dataset.gzReveal === 'headline' || element.dataset.gzReveal === 'media'
          ? element.parentElement
          : element;
      const targets = graziellaRevealTargets.get(observerTarget) || [];
      targets.push(element);
      graziellaRevealTargets.set(observerTarget, targets);
      graziellaRevealObserver.observe(observerTarget);
    });
  } catch (error) {
    document.documentElement.classList.remove('graziella-motion-ready');
    elements.forEach((element) => element.classList.add('is-visible'));
  }
}

function initializeGraziellaTilt(rootEl = document) {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches
  ) {
    return;
  }

  rootEl.querySelectorAll('[data-gz-tilt]').forEach((element) => {
    if (element.dataset.gzTiltReady === 'true') return;
    element.dataset.gzTiltReady = 'true';

    element.addEventListener('pointermove', (event) => {
      const bounds = element.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));

      element.style.setProperty('--gz-pointer-x', `${x * 100}%`);
      element.style.setProperty('--gz-pointer-y', `${y * 100}%`);
      element.style.setProperty('--gz-rotate-x', `${(0.5 - y) * 1.8}deg`);
      element.style.setProperty('--gz-rotate-y', `${(x - 0.5) * 1.8}deg`);
      element.style.setProperty('--gz-image-x', `${(0.5 - x) * 4}px`);
      element.style.setProperty('--gz-image-y', `${(0.5 - y) * 4}px`);
    });

    element.addEventListener('pointerleave', () => {
      element.style.setProperty('--gz-pointer-x', '50%');
      element.style.setProperty('--gz-pointer-y', '50%');
      element.style.setProperty('--gz-rotate-x', '0deg');
      element.style.setProperty('--gz-rotate-y', '0deg');
      element.style.setProperty('--gz-image-x', '0px');
      element.style.setProperty('--gz-image-y', '0px');
    });
  });
}

function initializeGraziellaHero(rootEl = document) {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    !window.matchMedia('(hover: hover) and (pointer: fine)').matches
  ) {
    return;
  }

  rootEl.querySelectorAll('[data-gz-hero]').forEach((hero) => {
    if (hero.dataset.gzHeroReady === 'true') return;
    hero.dataset.gzHeroReady = 'true';

    hero.addEventListener('pointermove', (event) => {
      const bounds = hero.getBoundingClientRect();
      const x = Math.min(1, Math.max(0, (event.clientX - bounds.left) / bounds.width));
      const y = Math.min(1, Math.max(0, (event.clientY - bounds.top) / bounds.height));

      hero.style.setProperty('--gz-aura-x', `${x * 100}%`);
      hero.style.setProperty('--gz-aura-y', `${y * 100}%`);
      hero.style.setProperty('--gz-hero-x', `${(x - 0.5) * -8}px`);
      hero.style.setProperty('--gz-hero-y', `${(y - 0.5) * -6}px`);
    });

    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--gz-aura-x', '65%');
      hero.style.setProperty('--gz-aura-y', '38%');
      hero.style.setProperty('--gz-hero-x', '0px');
      hero.style.setProperty('--gz-hero-y', '0px');
    });
  });
}

function initializeGraziellaProgress() {
  if (
    document.querySelector('.graziella-scroll-progress') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    return;
  }

  const progress = document.createElement('div');
  progress.className = 'graziella-scroll-progress';
  progress.setAttribute('aria-hidden', 'true');
  document.body.append(progress);

  let frame;
  const update = () => {
    const distance = document.documentElement.scrollHeight - window.innerHeight;
    const value = distance > 0 ? Math.min(1, Math.max(0, window.scrollY / distance)) : 0;
    progress.style.transform = `scaleX(${value})`;
    frame = undefined;
  };
  const queueUpdate = () => {
    if (frame) return;
    frame = requestAnimationFrame(update);
  };

  window.addEventListener('scroll', queueUpdate, { passive: true });
  window.addEventListener('resize', queueUpdate, { passive: true });
  update();
}

function initializeGraziellaTickers(rootEl = document) {
  rootEl.querySelectorAll('[data-gz-ticker]').forEach((ticker) => {
    if (ticker.dataset.gzTickerReady === 'true') return;
    const button = ticker.querySelector('[data-gz-ticker-toggle]');
    const icon = ticker.querySelector('[data-gz-ticker-icon]');
    if (!button || !icon) return;
    ticker.dataset.gzTickerReady = 'true';

    button.addEventListener('click', () => {
      const paused = ticker.classList.toggle('is-paused');
      button.setAttribute('aria-pressed', String(paused));
      button.setAttribute('aria-label', paused ? button.dataset.playLabel : button.dataset.pauseLabel);
      icon.textContent = paused ? '▶' : 'Ⅱ';
    });
  });
}

function initializeGraziellaMotion(rootEl = document) {
  initializeGraziellaReveal(rootEl);
  initializeGraziellaTilt(rootEl);
  initializeGraziellaHero(rootEl);
  initializeGraziellaTickers(rootEl);
  initializeGraziellaProgress();
}

window.addEventListener('DOMContentLoaded', () => initializeGraziellaMotion());

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeGraziellaMotion(event.target));
}
